import { aiserverbaselink } from '../../../Env_Variables';
import { aiInstituteHeaders } from './aiInstituteHeaders';

export const VOICE_NOTE_MAX_MS = 10000;
export const VOICE_NOTE_BARS = 36;

export function fallbackPeaks(seed = 1) {
    return Array.from({ length: VOICE_NOTE_BARS }, (_, i) => {
        const n = 0.28 + Math.abs(Math.sin(i * 0.62 + seed)) * 0.55;
        return Math.max(0.12, Math.min(1, n));
    });
}

export function mergePeaks(prev, next) {
    if (!next?.length) return prev || fallbackPeaks();
    if (!prev?.length) return next.slice();
    return next.map((value, i) => Math.min(1, Math.max(Number(value) || 0, (prev[i] || 0) * 0.88)));
}

export function createLiveAnalyser(stream) {
    try {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx || !stream) return null;
        const ctx = new Ctx();
        const source = ctx.createMediaStreamSource(stream);
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 128;
        analyser.smoothingTimeConstant = 0.62;
        source.connect(analyser);
        const data = new Uint8Array(analyser.frequencyBinCount);
        try {
            ctx.resume();
        } catch (_) {
            /* ignore */
        }
        return {
            sample() {
                analyser.getByteFrequencyData(data);
                const usable = data.subarray(2);
                const chunk = Math.max(1, Math.floor(usable.length / VOICE_NOTE_BARS));
                const bars = [];
                for (let i = 0; i < VOICE_NOTE_BARS; i += 1) {
                    let sum = 0;
                    for (let j = 0; j < chunk; j += 1) sum += usable[i * chunk + j] || 0;
                    bars.push(Math.min(1, sum / chunk / 168));
                }
                return bars;
            },
            stop() {
                try {
                    source.disconnect();
                } catch (_) {
                    /* ignore */
                }
                try {
                    ctx.close();
                } catch (_) {
                    /* ignore */
                }
            },
        };
    } catch (_) {
        return null;
    }
}

export function pickRecorderMime() {
    if (typeof MediaRecorder === 'undefined' || typeof MediaRecorder.isTypeSupported !== 'function') {
        return '';
    }
    const types = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus'];
    return types.find((type) => MediaRecorder.isTypeSupported(type)) || '';
}

export function recorderExtension(mime) {
    if (String(mime).includes('mp4')) return 'm4a';
    if (String(mime).includes('ogg')) return 'ogg';
    return 'webm';
}

export function formatNoteTimer(ms) {
    const total = Math.max(0, Math.floor(Number(ms) || 0) / 1000);
    const m = Math.floor(total / 60);
    const s = String(Math.floor(total % 60)).padStart(2, '0');
    return `${m}:${s}`;
}

export async function transcribeVoiceNote(blob, { durationMs, signal } = {}) {
    const mime = blob.type || 'audio/webm';
    const filename = `voice-note.${recorderExtension(mime)}`;
    const form = new FormData();
    form.append('audio', blob, filename);
    if (durationMs) form.append('duration_ms', String(durationMs));

    const response = await fetch(`${aiserverbaselink}/instowner/ai/transcribe`, {
        method: 'POST',
        credentials: 'include',
        headers: {
            Accept: 'application/json',
            ...aiInstituteHeaders(),
        },
        body: form,
        signal,
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || data.ok === false) {
        return {
            ok: false,
            error: data.error || 'Could not understand that voice note.',
        };
    }
    return data;
}
